import type { RoomActivity } from '@brush/api-client';

/*
 * The operator Overview's activity cards (ADR 0012 Decision 16, step 20): the room's own figures, counts
 * only, each month the room's own. Kept out of the component so each rule is a test rather than a reading
 * of markup. The one rule above the others is the admin Overview's: nothing here draws a zero for
 * something nobody has measured — last month is stored only once the room has closed it, and until then
 * it is said, never shown as 0, since a month nobody has measured is not a month nobody played in.
 */

/** A figure now beside last month's: two lines of words. */
export interface FigureLine {
  now: string;
  last: string;
}

/** A month's name from its first day, `yyyy-mm-01`, read in UTC so no zone moves it into the month before. */
function monthName(firstDay: string, monthsBack = 0): string {
  const [y, m] = firstDay.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 - monthsBack, 1)).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
}

/** Last month's half of a line: the stored figure in words, or that it is not measured yet. */
function lastMonth(activity: RoomActivity, words: string | null): string {
  const name = monthName(activity.month, 1);
  return words === null ? `${name} not measured yet` : `${words} in ${name}`;
}

/** Active players: the room's month so far, beside last month as stored. */
export function activePlayersLine(activity: RoomActivity): FigureLine {
  const { current, lastMonth: last } = activity.activePlayers;
  return {
    now: `${current} so far in ${monthName(activity.month)}`,
    last: lastMonth(activity, last === null ? null : `${last}`),
  };
}

/**
 * New and returning: this month's active players split by whether the room first saw them this month.
 * Last month is one stored split, so a month with either half missing is not measured.
 */
export function newReturningLine(activity: RoomActivity): FigureLine {
  const { newSoFar, returningSoFar, newLastMonth, returningLastMonth } = activity.newVsReturning;
  return {
    now: `${newSoFar} new · ${returningSoFar} returning so far in ${monthName(activity.month)}`,
    last: lastMonth(activity,
      newLastMonth === null || returningLastMonth === null ? null : `${newLastMonth} new · ${returningLastMonth} returning`),
  };
}

/** A feature's line: who used it this month so far, beside last month. */
export interface FeatureLine extends FigureLine {
  feature: string;
  title: string;
}

/** The room's three feature switches, as the room carries them. */
export interface RoomSwitches {
  canLeaderboard: boolean;
  canRatings: boolean;
  canFantasy: boolean;
}

/**
 * The portal features in the product's order, under the labels admin-web's Overview uses so the two
 * consoles word a feature alike, and the room switch each of the three switchable ones answers to.
 */
const FEATURES: readonly { feature: string; label: string; switchKey: keyof RoomSwitches | null }[] = [
  { feature: 'leaderboards', label: 'Leaderboards', switchKey: 'canLeaderboard' },
  { feature: 'ratings', label: 'Ratings', switchKey: 'canRatings' },
  { feature: 'fantasy', label: 'Fantasy', switchKey: 'canFantasy' },
  { feature: 'players', label: 'Players', switchKey: null },
  { feature: 'memberships', label: 'Connected accounts', switchKey: null },
  { feature: 'portal', label: 'Player portal', switchKey: null },
];

/**
 * Players who used each portal feature at the room — "players who used X", each counted once however often
 * they came back. A switchable feature the room has off gets no line: its zero would say nobody used
 * something the room does not offer. A feature this build does not know is shown under its own name, last,
 * rather than dropped — the server added it, so somebody may be using it.
 */
export function featureLines(activity: RoomActivity, room: RoomSwitches): FeatureLine[] {
  const known = new Map(FEATURES.map((f, i) => [f.feature, { ...f, order: i }]));
  return activity.featurePlayers
    .filter((f) => {
      const key = known.get(f.feature)?.switchKey;
      return !key || room[key];
    })
    .map((f) => ({ f, entry: known.get(f.feature) }))
    .sort((a, b) => (a.entry?.order ?? FEATURES.length) - (b.entry?.order ?? FEATURES.length))
    .map(({ f, entry }) => ({
      feature: f.feature,
      title: `Players who used ${entry?.label ?? f.feature}`,
      now: `${f.soFar} so far in ${monthName(activity.month)}`,
      last: lastMonth(activity, f.lastMonth === null ? null : `${f.lastMonth}`),
    }));
}

/** The connection rate as a Ratio now, and last month's in words. */
export interface ConnectionCard {
  claimed: number;
  cards: number;
  last: string;
}

/**
 * The connection rate: cards with a claimed account over cards in the room — a Ratio, never a bare
 * percentage, now and as stored at last month's close. Both counts or neither, so a half-stored month is
 * not measured.
 */
export function connectionCard(activity: RoomActivity): ConnectionCard {
  const { claimed, cards, lastMonthClaimed, lastMonthCards } = activity.connection;
  return {
    claimed,
    cards,
    last: lastMonth(activity,
      lastMonthClaimed === null || lastMonthCards === null ? null : `${lastMonthClaimed} of ${lastMonthCards} cards`),
  };
}

/** Dormant players: a count as of today, last month's, and what the figure means. */
export interface DormantCard extends FigureLine {
  definition: string;
}

/**
 * Dormant players: a state rather than a month's count, so "now" and "at the end of" last month. The
 * definition is on the card because the figure is not self-explaining, and it carries the six-hour note:
 * last month's figure was read from each player's last visit when the room's month closed — up to six
 * hours after it ended — so a player who came back in those hours counts as back. It errs toward fewer
 * dormant, never more.
 */
export function dormantCard(activity: RoomActivity): DormantCard {
  const { current, lastMonth: last } = activity.dormant;
  const name = monthName(activity.month, 1);
  return {
    now: `${current} now`,
    last: last === null ? `${name} not measured yet` : `${last} at the end of ${name}`,
    definition:
      "Players last seen at this room 31 to 60 days ago, and not since. Last month's figure was taken when " +
      "the room's month closed — up to six hours after it ended — so a player who came back in those hours " +
      'counts as back.',
  };
}

/**
 * An internal room's activity is counted nowhere (a test or demo room), so every figure on these cards is
 * zero by design. Said once, above them, so the zeros do not read as a quiet month.
 */
export function internalNotice(activity: RoomActivity): string | null {
  return activity.isInternal
    ? 'This room is marked internal, so its players’ activity is not counted — every figure below is zero by design.'
    : null;
}

/** A board behind its room's tournaments, in words. */
export interface BoardBehindLine {
  boardId: string;
  kind: string;
  name: string;
  says: string;
  next: string;
  lastRecalculatedAt: string;
}

/** The drift card: the boards behind, or why there are none, and what the count counts. */
export interface BoardsBehindCard {
  rows: BoardBehindLine[];
  empty: string | null;
  note: string;
}

/**
 * The drift card (ADR 0012 step 23): running boards that recalculate by hand or on a schedule, overtaken by
 * tournament changes in the room since their last snapshot. What comes next depends on how the board runs —
 * a manual board waits for somebody, a scheduled one catches up by itself — so each says which. The count
 * is tournaments, not edits, and it is the room's: a tournament the board does not score still counts, which
 * the note says rather than letting "2 changed" read as "2 of mine changed".
 */
export function boardsBehindCard(activity: RoomActivity): BoardsBehindCard {
  const rows = activity.boardsBehind.map((b) => ({
    boardId: b.boardId,
    kind: b.kind === 'rating' ? 'Rating' : 'Leaderboard',
    name: b.name,
    says: `${b.tournamentsChanged} ${b.tournamentsChanged === 1 ? 'tournament' : 'tournaments'} changed since it was last recalculated.`,
    next: b.recalcMode === 'manual'
      ? 'Recalculate it to bring it up to date.'
      : 'It catches up at its next scheduled recalculation.',
    lastRecalculatedAt: b.lastRecalculatedAt,
  }));
  return {
    rows,
    empty: rows.length === 0
      ? 'Every board recalculated by hand or on a schedule is up to date with this room’s tournaments.'
      : null,
    note:
      'Counts every tournament changed in this room — results, date, buy-in, tags, a delete — including ones a ' +
      'board does not score. Boards that recalculate when results are entered keep themselves up to date.',
  };
}

